<template>
  <div class="container">
    <Card>
      <div>
        <UiState v-if="!product && loading" type="loading" title="Loading product" compact />
        <UiState
          v-else-if="!product && loadError"
          type="error"
          title="Could not load product"
          :message="loadError"
          action-label="Retry"
          compact
          @action="fetchProduct"
        />
        <div class="d-flex justify-content-between" v-if="product">
          <h2 class="d-flex mb-3" style="gap: 15px">
            <strong class="text-primary"><router-link :to="ROUTE_PATHS.PRODUCTS">All Products</router-link></strong>
            <div><i class="fas fa-chevron-right"></i></div>
            <strong>{{ getDisplayName(product) }}</strong>
          </h2>
          <div>
            <CustomDropdown
              @click="handleDropdownClick"
              :options="options"
              title="Tools"
            />
          </div>
        </div>

        <hr />
        <UiState v-if="!product && !loading && !loadError" type="empty" title="Product not found" compact />
        <div class="my-3" v-if="product">
          <div class="row">
            <div class="col-md-3">
              <ProductVariantSelector
                :product="product"
                :variants="variants"
                :selected-product="selectedProduct"
                :get-display-name="getDisplayName"
                @select="selectedProduct = $event"
              />
            </div>
            <div class="col-md-9">
              <div>
                <ProductSelectedSummary
                  :selected-product="selectedProduct"
                  :is-variant="!!isVariant"
                  :theme="theme"
                  :themes="THEMES"
                  @add-variant="$router.push(productCreatePath(product.id))"
                  @archive="handleArchive"
                />
                <TabContainer
                  @change="handleChangeTab"
                  :tabs="
                    Object.values(PRODUCT_TABS).filter((item) => {
                      if (!isVariant && item.hideForProduct)
                        return !item.hideForProduct;
                      else return true;
                    })
                  "
                  :selectedTab="selectedTab"
                />
                <div class="mb-3"></div>
                <div
                  v-if="
                    selectedTab && selectedTab.name == PRODUCT_TABS.GENERAL.name
                  "
                >
                  <EditBtn :value="editMode" @input="handleEditModeInput" />
                  <div class="section-group">
                    <SectionTitle>Classification</SectionTitle>
                    <InfoListItem title="Industry" :value="storeName" />

                    <InfoListItem
                      title="Category"
                      :value="selectedProduct.item_group"
                    />
                    <div v-if="editMode && !isVariant">
                      <SelectDropdown
                        v-if="categoriesLoaded"
                        :value="item_group"
                        :options="
                          categories.map((item) => ({
                            id: item.name,
                            title: item.name,
                          }))
                        "
                        @change="handleCategorySelect"
                      />
                    </div>
                  </div>
                  <div class="section-group">
                    <SectionTitle>General Information</SectionTitle>
                    <InfoListItem
                      title="Product Name"
                      :value="selectedProduct.name || selectedProduct.item_name"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput v-model="name" />
                    </div>

                    <InfoListItem
                      title="Notes"
                      :value="selectedProduct.notes"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput v-model="notes" />
                    </div>
                    <InfoListItem
                      title="Short Description"
                      :value="selectedProduct.short_description"
                      :sliceTextLength="75"
                      :showViewMore="true"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput v-model="short_description" />
                    </div>

                    <InfoListItem
                      title="Long Description"
                      :value="selectedProduct.description"
                      :sliceTextLength="75"
                      :showViewMore="true"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput v-model="description" />
                    </div>

                    <InfoListItem
                      title="MSRP"
                      :value="selectedProduct.msrp"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="msrp" />
                    </div>

                    <InfoListItem
                      title="Release Date"
                      :value="selectedProduct.release_date"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="date" v-model="release_date" />
                    </div>
                  </div>
                  <div class="section-group">
                    <SectionTitle>Manufacturer SKUs</SectionTitle>

                    <CustomTable>
                      <template #head>
                        <tr>
                          <th>SKU</th>
                          <th>Description</th>
                        </tr>
                      </template>
                      <template #body>
                        <tr>
                          <td>
                            <div v-if="!editMode">
                              {{ manufacturer_sku }}
                            </div>
                            <CustomInput v-else v-model="manufacturer_sku" />
                          </td>
                          <td>
                            <div v-if="!editMode">
                              {{ manufacturer_sku_description }}
                            </div>
                            <CustomInput
                              v-else
                              v-model="manufacturer_sku_description"
                            />
                          </td>
                        </tr>
                      </template>
                    </CustomTable>
                  </div>
                  <div class="section-group" v-if="isVariant">
                    <SectionTitle>Supplier SKUs</SectionTitle>

                    <CustomTable>
                      <template #head>
                        <tr>
                          <th>SKU</th>
                          <th>Supplier</th>
                          <th>Description</th>
                        </tr>
                      </template>
                      <template #body>
                        <tr>
                          <td>
                            <div v-if="!editMode">
                              {{ supplier_sku }}
                            </div>
                            <CustomInput v-else v-model="supplier_sku" />
                          </td>
                          <td>
                            <div v-if="!editMode">
                              {{ supplier_sku_name }}
                            </div>
                            <SelectDropdown
                             v-else
                              normal
                              :options="
                                suppliers.map((item) => ({
                                  id: item.id,
                                  title: item.name,
                                }))
                              "
                              :value="supplier_sku_name"
                              @change="supplier_sku_name = $event"
                            />
                            <!-- <CustomInput v-else v-model="supplier_sku_name" /> -->
                          </td>
                          <td>
                            <div v-if="!editMode">
                              {{ supplier_sku_description }}
                            </div>
                            <CustomInput
                              v-else
                              v-model="supplier_sku_description"
                            />
                          </td>
                        </tr>
                      </template>
                    </CustomTable>
                  </div>
                  <div class="section-group" v-if="isVariant">
                    <SectionTitle>UPCs</SectionTitle>

                    <CustomTable>
                      <template #head>
                        <tr>
                          <th>UPC</th>
                          <th>Case GTIN</th>
                          <th>Each GTIN</th>
                          <th>GS1 Barcode</th>
                          <th>Description</th>
                        </tr>
                      </template>
                      <template #body>
                        <tr>
                          <td>
                            <div v-if="!editMode">{{ upc }}</div>
                            <CustomInput v-else v-model="upc" />
                          </td>
                          <td>
                            <div v-if="!editMode">{{ casegtin }}</div>
                            <CustomInput v-else v-model="casegtin" />
                          </td>
                          <td>
                            <div v-if="!editMode">{{ eachgtin }}</div>
                            <CustomInput v-else v-model="eachgtin" />
                          </td>
                          <td>
                            <div v-if="!editMode">{{ gtinbarcode }}</div>
                            <CustomInput v-else v-model="gtinbarcode" />
                          </td>
                          <td>
                            <div v-if="!editMode">
                              {{ upc_description }}
                            </div>
                            <CustomInput v-else v-model="upc_description" />
                          </td>
                        </tr>
                      </template>
                    </CustomTable>
                  </div>

                  <ProductEditActions
                    :edit-mode="editMode"
                    :processing="processing"
                    @cancel="cancelEditing"
                    @save="updateProduct"
                  />
                </div>
                <div
                  v-else-if="
                    selectedTab &&
                    selectedTab.name == PRODUCT_TABS.SPECIFICATION.name
                  "
                >
                  <EditBtn :value="editMode" @input="handleEditModeInput" />
                  <div
                    class="section-group"
                    v-if="selectedProduct.batch_tracking"
                  >
                    <SectionTitle>Details</SectionTitle>

                    <InfoListItem
                      title="Retail Marijuana Product Type Canada"
                      :value="retail_marijuana_product_type__canada"
                      :editMode="editMode"
                    />

                    <div v-if="editMode">
                      <SelectDropdown
                        :value="retail_marijuana_product_type__canada"
                        :options="
                          marijuanaProductType.map((item) => ({
                            id: item.title,
                            title: item.title,
                          }))
                        "
                        @change="retail_marijuana_product_type__canada = $event"
                      />
                    </div>

                    <InfoListItem
                      title="Equivalent To"
                      :value="
                        selectedProduct.equivalent_to
                          ? selectedProduct.equivalent_to + ' g'
                          : ''
                      "
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="equivalent_to" />
                    </div>

                    <InfoListItem
                      title="Strain"
                      :value="strain"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="strain" />
                    </div>

                    <InfoListItem
                      title="Health Canada Reporting Category"
                      :value="health_canada_reporting_category"
                      :editMode="editMode"
                    />

                    <div v-if="editMode">
                      <SelectDropdown
                        :value="health_canada_reporting_category"
                        :options="
                          healthCanadaReportingCategory.map((item) => ({
                            id: item.title,
                            title: item.title,
                          }))
                        "
                        @change="health_canada_reporting_category = $event"
                      />
                    </div>

                    <InfoListItem
                      title="Net Weight"
                      :value="
                        selectedProduct.net_weight
                          ? selectedProduct.net_weight + ' g'
                          : ''
                      "
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="net_weight" />
                    </div>

                    <InfoListItem
                      title="Volume"
                      :value="selectedProduct.volume"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="volume" />
                    </div>
                  </div>

                  <div
                    class="section-group"
                    v-if="selectedProduct.batch_tracking"
                  >
                    <SectionTitle>Additional Details</SectionTitle>
                    <InfoListItem
                      title="Manufacturer"
                      :value="selectedProduct.manufacturer"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="manufacturer" />
                    </div>

                    <InfoListItem
                      title="Brand"
                      :value="selectedProduct.brand"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="brand" />
                    </div>

                    <!-- <InfoListItem
                                            title="Terpenes"
                                            :value="selectedProduct.terpenes"
                                            :editMode="editMode"
                                        />
                                        <div v-if="editMode">
                                            <CustomInput type="text" v-model="terpenes" />
                                        </div> -->

                    <InfoListItem
                      title="Terpenes"
                      :value="selectedTerpenes"
                      :editMode="editMode"
                    />

                    <div v-if="editMode">
                      <SelectDropdown
                        :value="terpenes"
                        :multiple="true"
                        :options="
                          terpenesMaster.map((item) => ({
                            id: item.id,
                            title: item.name,
                          }))
                        "
                        @change="terpenes = $event"
                      />
                    </div>

                    <InfoListItem
                      title="THC Min"
                      :value="selectedProduct.thc_min"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="thc_min" />
                    </div>

                    <InfoListItem
                      title="THC Max"
                      :value="selectedProduct.thc_max"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="thc_max" />
                    </div>

                    <InfoListItem
                      title="CBD Min"
                      :value="selectedProduct.cbd_min"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="cbd_min" />
                    </div>

                    <InfoListItem
                      title="CBD Max"
                      :value="selectedProduct.cbd_max"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="number" v-model="cbd_max" />
                    </div>
                  </div>

                  <div class="section-group">
                    <SectionTitle>Online Menu</SectionTitle>

                    <InfoListItem
                      title="Online Menu Name"
                      :value="selectedProduct.online_menu_name"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="online_menu_name" />
                    </div>

                    <InfoListItem
                      title="Online Menu Brand"
                      :value="selectedProduct.online_menu_brand"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="online_menu_brand" />
                    </div>

                    <InfoListItem
                      title="Online Menu Category"
                      :value="selectedProduct.online_menu_category"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <CustomInput type="text" v-model="online_menu_category" />
                    </div>
                  </div>

                  <ProductEditActions
                    :edit-mode="editMode"
                    :processing="processing"
                    @cancel="cancelEditing"
                    @save="updateProduct"
                  />
                </div>
                <div
                  v-else-if="
                    selectedTab &&
                    selectedTab.name == PRODUCT_TABS.PRODUCT_BEHAVIOUR.name
                  "
                >
                  <EditBtn :value="editMode" @input="handleEditModeInput" />

                  <div class="section-group">
                    <SectionTitle>Product Behaviours</SectionTitle>

                    <InfoListItem
                      title="Unit of Measure"
                      :value="selectedProduct.weight_uom"
                      :editMode="editMode"
                    />

                    <div v-if="editMode">
                      <SelectDropdown
                        :options="
                          weightUoms.map((item) => ({
                            id: item.title,
                            title: item.title,
                          }))
                        "
                        :value="weight_uom"
                        @change="weight_uom = $event"
                      />
                    </div>

                    <InfoListItem
                      :title="
                        allowBatchTracking
                          ? 'Batch Tracking'
                          : 'Batch Tracking (Disabled)'
                      "
                      :value="
                        selectedProduct.batch_tracking == 1 ? 'On' : 'Off'
                      "
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <input
                        :disabled="!allowBatchTracking"
                        :value="1"
                        type="checkbox"
                        v-model="batch_tracking"
                      />
                    </div>

                    <InfoListItem
                      title="Non Stock"
                      :value="selectedProduct.non_stock == 1 ? 'On' : 'Off'"
                      :editMode="editMode"
                    />
                    <div v-if="editMode">
                      <input :value="1" type="checkbox" v-model="non_stock" />
                    </div>
                  </div>
                  <ProductEditActions
                    :edit-mode="editMode"
                    :processing="processing"
                    @cancel="cancelEditing"
                    @save="updateProduct"
                  />
                </div>
                <div
                  v-else-if="
                    selectedTab && selectedTab.name == PRODUCT_TABS.PRICING.name
                  "
                >
                  <div class="section-group">
                    <div class="section-title mb-3">Pricing</div>
                    <PricingForm
                      v-if="selectedProduct.item_code"
                      :warehouse="null"
                      :item_id="selectedProduct.id"
                      :item_code="selectedProduct.item_code"
                      :variations="variants"
                    />
                    <div v-else>Item code not found</div>
                  </div>
                  <ProductEditActions
                    :edit-mode="editMode"
                    :processing="processing"
                    @cancel="cancelEditing"
                    @save="updateProduct"
                  />
                </div>
                <div
                  v-else-if="
                    selectedTab && selectedTab.name == PRODUCT_TABS.ASSETS.name
                  "
                >
                  <ProductAssetsTab
                    :selected-product="selectedProduct"
                    :server-url="serverURL"
                    :processing="processing"
                    @image-selected="productImage = $event"
                    @update="updateProduct"
                  />
                </div>
                <div
                  v-else-if="
                    selectedTab &&
                    selectedTab.name == PRODUCT_TABS.AVAILABILITY.name
                  "
                >
                  <ProductAvailabilityTab
                    :warehouses="warehouses"
                    :selected-product="selectedProduct"
                    :stock-data="stockData"
                  />
                </div>
                <div class="my-3" v-else>Coming soon...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import {
  confirmAction,
} from "../../utils/common";
import { http, serverURL } from "@/utils/http";
import { mapState } from "vuex";
import TabContainer from "../../components/UI/TabContainer.vue";
import CustomInput from "../../components/crud/custom-input.vue";
import EditBtn from "../../components/UI/EditBtn.vue";
import SelectDropdown from "../../components/crud/SelectDropdown.vue";
import InfoListItem from "../../components/UI/InfoListItem.vue";
import CustomTable from "../../components/layout/CustomTable.vue";
import PricingForm from "../../components/product/PricingForm.vue";
import Card from "../../components/UI/Card.vue";
import SectionTitle from "../../components/UI/SectionTitle.vue";
import CustomDropdown from "../../components/layout/CustomDropdown";
import UiState from "../../components/UI/UiState.vue";
import ProductVariantSelector from "../../components/product/ProductVariantSelector.vue";
import ProductAssetsTab from "../../components/product/ProductAssetsTab.vue";
import ProductAvailabilityTab from "../../components/product/ProductAvailabilityTab.vue";
import ProductSelectedSummary from "../../components/product/ProductSelectedSummary.vue";
import ProductEditActions from "../../components/product/ProductEditActions.vue";
import { ROUTE_PATHS, productCreatePath, productDetailPath, printLabelsProductPath } from "../../router/route-paths";
import { getProductDisplayName } from "../../utils/product-display";

const PRODUCT_TABS = {
  GENERAL: {
    name: "GENERAL",
    value: "General",
    hideForProduct: 0,
  },
  SPECIFICATION: {
    name: "SPECIFICATION",
    value: "Specifications",
    hideForProduct: 0,
  },
  ASSETS: {
    name: "ASSETS",
    value: "Assets",
    hideForProduct: 0,
  },
  PRODUCT_BEHAVIOUR: {
    name: "PRODUCT_BEHAVIOUR",
    value: "Product Behaviours",
    hideForProduct: 1,
  },
  AVAILABILITY: {
    name: "AVAILABILITY",
    value: "Availability",
    hideForProduct: 1,
  },
  PRICING: {
    name: "PRICING",
    value: "Pricing",
    hideForProduct: 1,
  },
  MARKETING: {
    name: "MARKETING",
    value: "Marketing",
    hideForProduct: 0,
  },
};

const OPTIONS = { PRINT_LABELS: "PRINT_LABELS" };

export default {
  data() {
    return {
      storeName: process.env.VUE_APP_STORE_NAME || "Pos Store",
      allowBatchTracking: false,
      serverURL,
      selectedTab: PRODUCT_TABS.GENERAL,
      PRODUCT_TABS,
      productImage: null,
      product: null,
      loading: false,
      loadError: "",
      variants: [],
      selectedProduct: null,
      editMode: false,
      processing: false,
      categories: [],
      categoriesLoaded: false,
      item_group: "",
      name: "",
      notes: "",
      short_description: "",
      description: "",
      selling_price: "",
      weight: "",
      supplier_sku: "",
      supplier_sku_name: "",
      supplier_sku_description: "",
      manufacturer_sku: "",
      manufacturer_sku_description: "",
      upc: "",
      casegtin: "",
      eachgtin: "",
      gtinbarcode: "",
      upc_description: "",
      msrp: "",
      release_date: "",
      retail_marijuana_product_type__canada: "",
      equivalent_to: "",
      strain: "",
      health_canada_reporting_category: "",
      net_weight: "",
      volume: "",
      manufacturer: "",
      brand: "",
      terpenes: "",
      thc_min: "",
      thc_max: "",
      cbd_min: "",
      cbd_max: "",
      online_menu_name: "",
      online_menu_brand: "",
      online_menu_category: "",
      weight_uom: "",
      batch_tracking: 0,
      non_stock: 0,
      stockData: null,
      options: [{ id: OPTIONS.PRINT_LABELS, title: "Print Labels" }],
      ROUTE_PATHS,
    };
  },
  components: {
    CustomDropdown,
    SectionTitle,
    CustomInput,
    CustomTable,
    PricingForm,
    TabContainer,
    EditBtn,
    SelectDropdown,
    InfoListItem,
    Card,
    UiState,
    ProductVariantSelector,
    ProductAssetsTab,
    ProductAvailabilityTab,
    ProductSelectedSummary,
    ProductEditActions,
  },
  async mounted() {
    await this.fetchProduct();
    await this.fetchCategories();
    await this.checkBatchTrackingToAllow();
  },
  computed: {
    isVariant() {
      return this.selectedProduct && this.selectedProduct.parent;
    },
    selectedTerpenes() {
      return this.terpenes
        .map((item) => this.terpenesMaster.find((tm) => tm.id == item)?.name || String(item || "").trim())
        .filter(Boolean)
        .join(", ");
    },
    ...mapState("misc", [
      "healthCanadaReportingCategory",
      "marijuanaProductType",
      "terpenesMaster",
      "yesNoList",
      "weightUoms",
      "warehouses",
      "theme",
      "THEMES",
      "suppliers"
    ]),
  },
  watch: {
    async "$route.params.id"(id, previousId) {
      if (id === previousId) return;
      this.editMode = false;
      this.selectedTab = PRODUCT_TABS.GENERAL;
      await this.fetchProduct();
      await this.checkBatchTrackingToAllow();
    },
    selectedProduct(e) {
      this.applySelectedProductToDraft(e);
    },
  },
  methods: {
    getDisplayName: getProductDisplayName,
    applySelectedProductToDraft(e) {
      if (!e) return;
      this.item_group = e.item_group || "";
      this.name = e.name || e.item_name || "";
      this.notes = e.notes || "";
      this.description = e.description || "";
      this.short_description = e.short_description || "";
      this.selling_price = e.selling_price || "";
      this.weight = e.weight || "";

      // sku related
      this.supplier_sku = e.supplier_sku || "";
      this.supplier_sku_name = e.supplier_sku_name || "";
      this.supplier_sku_description = e.supplier_sku_description || "";
      this.manufacturer_sku = e.manufacturer_sku || "";
      this.manufacturer_sku_description = e.manufacturer_sku_description || "";
      this.upc = e.upc || "";
      this.casegtin = e.casegtin || "";
      this.eachgtin = e.eachgtin || e._eachgtin || "";
      this.gtinbarcode = e.gtinbarcode || "";
      this.upc_description = e.upc_description || "";

      this.msrp = e.msrp || "";
      this.release_date = e.release_date || "";

      // product details specification
      this.retail_marijuana_product_type__canada =
        e.retail_marijuana_product_type__canada || "";
      this.equivalent_to = e.equivalent_to || "";
      this.strain = e.strain || "";
      this.health_canada_reporting_category =
        e.health_canada_reporting_category || "";
      this.net_weight = e.net_weight || "";
      this.volume = e.volume || "";

      // additional details
      this.manufacturer = e.manufacturer || "";
      this.brand = e.brand || "";
      this.terpenes = (e.terpenes || "").split(",");
      this.thc_min = e.thc_min || "";
      this.thc_max = e.thc_max || "";
      this.cbd_min = e.cbd_min || "";
      this.cbd_max = e.cbd_max || "";

      // online menu
      this.online_menu_name = e.online_menu_name || "";
      this.online_menu_brand = e.online_menu_brand || "";
      this.online_menu_category = e.online_menu_category || "";

      // product behaviours
      this.weight_uom = e.weight_uom || "";
      this.batch_tracking = e.batch_tracking == 1;
      this.non_stock = e.non_stock == 1;
    },
    cancelEditing() {
      this.applySelectedProductToDraft(this.selectedProduct);
      this.editMode = false;
    },
    handleEditModeInput(nextEditMode) {
      if (nextEditMode) {
        this.editMode = true;
        return;
      }
      this.cancelEditing();
    },
    handleDropdownClick(item) {
      if (item.id == OPTIONS.PRINT_LABELS) {
        this.$router.push(printLabelsProductPath(this.selectedProduct.id));
      }
    },
    async checkBatchTrackingToAllow() {
      if (!this.selectedProduct || !this.selectedProduct.item_code) {
        this.allowBatchTracking = false;
        return;
      }
      try {
        const res = await http.get(
          `/items/check-batch-tracking-allowed/${this.selectedProduct.item_code}`
        );
        const result = res.data.result;
        this.allowBatchTracking = result;
      } catch (err) {
        this.allowBatchTracking = false;
        console.log("error in checking batch tracking to allow", err);
      }
    },
    handleChangeTab($event) {
      this.cancelEditing();
      this.selectedTab = $event;
    },
    async handleArchive() {
      if (!this.selectedProduct) return;
      await confirmAction(
        "Archived",
        this.isVariant
          ? "Variant archived successfully. It remains available in reports but cannot be sold."
          : "Product and its variations archived successfully. They remain available in reports but cannot be sold.",
        async () => {
          await http.post(`/items/${this.selectedProduct.id}/archive`);
        },
        this.isVariant
          ? "This variation will be disabled from selling. Its records and history will be retained."
          : "This product and all of its variations will be disabled from selling. Their records and history will be retained."
      );
      if (!this.isVariant) {
        this.$router.push(ROUTE_PATHS.PRODUCTS);
      } else {
        const parentId = this.selectedProduct.parent;
        this.$router.push(parentId ? productDetailPath(parentId) : ROUTE_PATHS.PRODUCTS);
      }
    },
    productCreatePath,
    handleCategorySelect(e) {
      console.log("handle category select.", e);
      this.item_group = e;
    },
    async fetchProduct() {
      this.loading = true;
      this.loadError = "";
      this.product = null;
      this.selectedProduct = null;
      this.variants = [];
      this.stockData = null;
      try {
        const res = await http.get(`/items/${this.$route.params.id}`);
        this.product = res.data.result;
        this.selectedProduct = this.product;
        this.variants = res.data.variants;
        this.stockData = res.data.stockData;
      } catch (err) {
        console.log("error in loading items", err);
        this.loadError = "Try again or return to the product list.";
      }
      this.loading = false;
    },
    async updateProduct() {
      if (this.processing) return false;
      this.processing = true;
      try {
        const formData = new FormData();
        formData.append("image", this.productImage);

        formData.append("name", this.name);
        formData.append("notes", this.notes);
        formData.append("item_group", this.item_group);
        formData.append("description", this.description);
        formData.append("msrp", this.msrp);
        formData.append("release_date", this.release_date);
        formData.append("short_description", this.short_description);
        formData.append("selling_price", this.selling_price);
        formData.append("weight", this.weight);

        formData.append("supplier_sku", this.supplier_sku);
        formData.append("supplier_sku_name", this.supplier_sku_name);
        formData.append(
          "supplier_sku_description",
          this.supplier_sku_description
        );
        formData.append("manufacturer_sku", this.manufacturer_sku);
        formData.append(
          "manufacturer_sku_description",
          this.manufacturer_sku_description
        );
        formData.append("upc", this.upc);
        formData.append("casegtin", this.casegtin);
        formData.append("eachgtin", this.eachgtin);
        formData.append("gtinbarcode", this.gtinbarcode);
        formData.append("upc_description", this.upc_description);

        // product details specification
        formData.append(
          "retail_marijuana_product_type__canada",
          this.retail_marijuana_product_type__canada
        );
        formData.append("equivalent_to", this.equivalent_to);
        formData.append("strain", this.strain);
        formData.append(
          "health_canada_reporting_category",
          this.health_canada_reporting_category
        );
        formData.append("net_weight", this.net_weight);
        formData.append("volume", this.volume);

        // additional details
        formData.append("manufacturer", this.manufacturer);
        formData.append("brand", this.brand);
        formData.append("terpenes", this.terpenes.join(","));
        formData.append("thc_min", this.thc_min);
        formData.append("thc_max", this.thc_max);
        formData.append("cbd_min", this.cbd_min);
        formData.append("cbd_max", this.cbd_max);

        // online menu
        formData.append("online_menu_name", this.online_menu_name);
        formData.append("online_menu_brand", this.online_menu_brand);
        formData.append("online_menu_category", this.online_menu_category);

        // product behaviours
        formData.append("weight_uom", this.weight_uom);
        formData.append("batch_tracking", this.batch_tracking ? 1 : 0);
        formData.append("non_stock", this.non_stock ? 1 : 0);

        const res = await http.patch(
          `/items/${this.selectedProduct.id}`,
          formData
        );
        this.product = res.data.result;
        this.variants = res.data.variants;
        this.selectedProduct =
          this.selectedProduct && this.product.id == this.selectedProduct.id
            ? this.product
            : this.variants.find((item) => item.id == this.selectedProduct.id);

        this.$toast.success("Updated Successfully!");
        this.editMode = false;
      } catch (err) {
        console.log("error in loading items", err);
      }
      this.processing = false;
    },
    async fetchCategories() {
      try {
        const res = await http.get(`/item-groups`);
        this.categories = res.data.result;
      } catch (err) {
        console.log("error in loading items", err);
      }
      this.categoriesLoaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./product-detail.scss";
</style>
